import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import { showElementSummary } from "core-integration/src/store/elements/thunks";
import { getTransfersForEntry } from "core-integration/src/store/entries/reducers";
import {
  fetchEntrySummary,
  fetchEntryTransfers,
} from "core-integration/src/store/entries/thunks";
import { getEventsById } from "core-integration/src/store/events/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { getLatestTransfers } from "core-integration/src/store/squad/reducers";
import { fetchLatestTransfers } from "core-integration/src/store/squad/thunks";
import moment from "moment";
import React, { useEffect } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { getPrefixedEventShortName } from "../utils/events";
import Entry from "./Entry";
import { Main, Secondary, Wrapper } from "./Layout";
import Table from "./Table";
import Title from "./Title";

const DialogButton = styled.button`
  padding: 0;
  border: 0;
  background-color: transparent;
  font-size: inherit;
  text-decoration: none;
  text-align: left;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

type Props = WithTranslation;

const TransfersHistory: React.FC<Props> = ({ t }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const { entryId } = useParams();
  const player = useSelector((state: RootState) => getPlayerData(state));
  const mine =
    player && player.entry && player.entry === Number(entryId) ? true : false;
  const latestTransfers = useSelector((state: RootState) =>
    mine ? getLatestTransfers(state) : [],
  );

  const elementsById = useSelector((state: RootState) =>
    getElementsById(state),
  );

  const eventsById = useSelector((state: RootState) => getEventsById(state));
  const transfersHistory = useSelector((state: RootState) =>
    getTransfersForEntry(state, Number(entryId)),
  );

  const showElementDialog = (elementId: number) =>
    dispatch(showElementSummary(elementId));

  useEffect(() => {
    dispatch(fetchEntrySummary(Number(entryId)));
    dispatch(fetchEntryTransfers(Number(entryId)));
    if (mine) {
      dispatch(fetchLatestTransfers());
    }
  }, [dispatch, entryId, mine]);

  return (
    <Wrapper>
      <Main>
        <Box mx={2}>
          <Title>{t("transfersHistory.title", "Transactions")}</Title>
        </Box>
        {transfersHistory.length || latestTransfers.length ? (
          <>
            <Box mb={2}>
              <Table>
                <thead>
                  <tr>
                    <th>{t("transfersHistory.time", "Time")}</th>
                    <th>{t("transfersHistory.in", "In")}</th>
                    <th>{t("transfersHistory.out", "Out")}</th>
                    <th>{t("transfersHistory.active", "Active")}</th>
                  </tr>
                </thead>
                <tbody>
                  {latestTransfers.concat(transfersHistory).map((t) => (
                    <tr key={`${t.time}${t.element_in}`}>
                      <td>{moment(t.time).format("D MMM HH:mm")}</td>
                      <td>
                        <DialogButton
                          onClick={() =>
                            showElementDialog(elementsById[t.element_in].id)
                          }
                        >
                          {elementsById[t.element_in].web_name}
                        </DialogButton>
                      </td>
                      <td>
                        <DialogButton
                          onClick={() =>
                            showElementDialog(elementsById[t.element_out].id)
                          }
                        >
                          {elementsById[t.element_out].web_name}
                        </DialogButton>
                      </td>
                      <td>
                        {getPrefixedEventShortName(eventsById[t.event].name)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Box>
            <Box mx={2}>
              <p>
                {t(
                  "transfersHistory.p1",
                  "If you are not logged in or are viewing another player's team then you will only be able to view transactions up to the last deadline.",
                )}
              </p>
            </Box>
          </>
        ) : (
          <Box mx={2}>
            {t(
              "transfersHistory.p2",
              "No transactions have been made yet for this team.",
            )}
          </Box>
        )}
      </Main>
      <Secondary>
        <Entry entryId={Number(entryId)} />
      </Secondary>
    </Wrapper>
  );
};

export default withTranslation()(TransfersHistory);
